import { defineStore } from 'pinia'
import { OdkForm, OdkFormStateUpdatePayload } from '../pages/projects/forms/types'
import {
  getOrganizationProjectForms,
  getOrganizationProjectFormDetails,
  deleteOrganizationProjectForm,
  updateOrganizationProjectFormState,
  publishOdkDraftForm,
  getOrganizationProjectFormAnalytics,
} from '../services/api'

interface FormsState {
  items: Record<string, OdkForm[]>
  detailsByKey: Record<string, OdkForm>
  loading: boolean
  error: string | null
}

function formDetailsKey(organizationId: number, projectId: number, xmlFormId: string): string {
  return `${organizationId}-${projectId}-${xmlFormId}`
}

export const useFormsStore = defineStore('forms', {
  state: (): FormsState => {
    return {
      items: {}, // Формы по проектам (ключ: 'orgId-projectId')
      detailsByKey: {}, // Детали форм (ключ: 'orgId-projectId-xmlFormId')
      loading: false,
      error: null,
    }
  },

  getters: {
    // Получение форм для конкретного проекта
    getProjectForms: (state) => (organizationId: number, projectId: number) => {
      const key = `${organizationId}-${projectId}`
      return state.items[key] || []
    },

    getCachedFormDetails: (state) => (organizationId: number, projectId: number, xmlFormId: string) => {
      return state.detailsByKey[formDetailsKey(organizationId, projectId, xmlFormId)] || null
    },

    // Получение черновиков форм
    getDraftForms: (state) => (organizationId: number, projectId: number) => {
      const key = `${organizationId}-${projectId}`
      const forms = state.items[key] || []
      return forms.filter((form: OdkForm) => !form.publishedAt)
    },

    // Получение опубликованных форм
    getPublishedForms: (state) => (organizationId: number, projectId: number) => {
      const key = `${organizationId}-${projectId}`
      const forms = state.items[key] || []
      return forms.filter((form: OdkForm) => !!form.publishedAt)
    },
  },

  actions: {
    /**
     * Load form details once per org/project/xmlFormId; reuse across form sections.
     */
    async fetchFormDetails(
      organizationId: number,
      projectId: number,
      xmlFormId: string,
      options: { force?: boolean } = {},
    ): Promise<OdkForm> {
      const key = formDetailsKey(organizationId, projectId, xmlFormId)
      if (!options.force && this.detailsByKey[key]) {
        return this.detailsByKey[key]
      }

      const details = await getOrganizationProjectFormDetails(organizationId, projectId, xmlFormId)
      this.detailsByKey[key] = details
      return details
    },

    setFormDetails(organizationId: number, projectId: number, form: OdkForm) {
      if (!form?.xmlFormId) return
      this.detailsByKey[formDetailsKey(organizationId, projectId, form.xmlFormId)] = form
    },

    invalidateFormDetails(organizationId: number, projectId: number, xmlFormId?: string) {
      if (xmlFormId) {
        delete this.detailsByKey[formDetailsKey(organizationId, projectId, xmlFormId)]
        return
      }
      const prefix = `${organizationId}-${projectId}-`
      for (const key of Object.keys(this.detailsByKey)) {
        if (key.startsWith(prefix)) {
          delete this.detailsByKey[key]
        }
      }
    },

    // Загрузка форм для проекта
    async fetchProjectForms(organizationId: number, projectId: number) {
      if (!organizationId || !projectId) return

      const key = `${organizationId}-${projectId}`
      this.loading = true
      this.error = null

      try {
        const forms = await getOrganizationProjectForms(organizationId, projectId)
        this.items[key] = forms
      } catch (err: any) {
        console.error('Error fetching forms:', err)
        this.error = err.message || 'Failed to fetch forms.'
        throw err
      } finally {
        this.loading = false
      }
    },

    // Публикация черновика формы
    async publishDraftForm(organizationId: number, projectId: number, form: OdkForm, version?: string) {
      console.log('[publishDraftForm Store Action] Called')

      if (!organizationId || !projectId || !form) {
        console.error('[publishDraftForm Store Action] Missing organizationId, projectId, or form')
        return false
      }

      console.log(`[publishDraftForm Store Action] Attempting to publish form:`, {
        organizationId,
        projectId,
        form,
        version,
      })
      this.loading = true
      this.error = null

      try {
        await publishOdkDraftForm(organizationId, projectId, form.xmlFormId, version)

        console.log(`[publishDraftForm Store Action] Form publish successful for xmlFormId: ${form.xmlFormId}`)

        await this.fetchProjectForms(organizationId, projectId)

        return true
      } catch (error) {
        console.error('[publishDraftForm Store Action] Caught error during publish operation:', error)
        this.error = error instanceof Error ? error.message : 'Failed to publish form.'
        return false
      } finally {
        this.loading = false
      }
    },

    // Удаление формы
    async deleteForm(organizationId: number, projectId: number, form: OdkForm) {
      if (!organizationId || !projectId || !form) return

      try {
        const result = await deleteOrganizationProjectForm(organizationId, projectId, form.xmlFormId)

        // После успешного удаления обновляем кеш форм
        const key = `${organizationId}-${projectId}`
        if (this.items[key]) {
          this.items[key] = this.items[key].filter((f) => f.xmlFormId !== form.xmlFormId)
        }
        this.invalidateFormDetails(organizationId, projectId, form.xmlFormId)

        return result
      } catch (error) {
        console.error('Error deleting form:', error)
        throw error
      }
    },

    // Изменение состояния формы
    async changeFormState(
      organizationId: number,
      projectId: number,
      form: OdkForm,
      newState: OdkFormStateUpdatePayload,
    ) {
      if (!organizationId || !projectId || !form) return

      try {
        const updatedForm = await updateOrganizationProjectFormState(
          organizationId,
          projectId,
          form.xmlFormId,
          newState,
        )

        // Обновляем форму в кеше
        const key = `${organizationId}-${projectId}`
        if (this.items[key]) {
          const index = this.items[key].findIndex((f) => f.xmlFormId === form.xmlFormId)
          if (index !== -1) {
            this.items[key][index] = updatedForm
          }
        }

        return updatedForm
      } catch (error) {
        console.error('Error changing form state:', error)
        throw error
      }
    },

    // Получение аналитики формы
    async getFormAnalytics(organizationId: number, projectId: number, xmlFormId: string) {
      if (!organizationId || !projectId || !xmlFormId) {
        throw new Error('Не указаны обязательные параметры для получения аналитики формы')
      }

      try {
        const analytics = await getOrganizationProjectFormAnalytics(organizationId, projectId, xmlFormId)
        return analytics
      } catch (error) {
        console.error('Error fetching form analytics:', error)
        throw error
      }
    },

    // Очистка кеша форм для проекта
    clearProjectForms(organizationId: number, projectId: number) {
      const key = `${organizationId}-${projectId}`
      if (this.items[key]) {
        delete this.items[key]
      }
      this.invalidateFormDetails(organizationId, projectId)
    },

    // Очистка всего кеша форм
    clearAllForms() {
      this.items = {}
      this.detailsByKey = {}
    },
  },
})
